
import { useState, useEffect } from "react";
import { Mail, Lock, LogIn, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/components/ui/use-toast";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/auth";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";

interface LoginFormProps {
  onLoginSuccess: () => void;
  isLoading: boolean;
  setIsLoading: (isLoading: boolean) => void;
}

export const LoginForm = ({ onLoginSuccess, isLoading, setIsLoading }: LoginFormProps) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showConfirmationAlert, setShowConfirmationAlert] = useState(false);
  const { toast } = useToast();
  const { signIn } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Vérifier si l'utilisateur vient de s'inscrire et a besoin de confirmer son email
  useEffect(() => {
    if (location.state?.needsConfirmation) {
      console.log("Alerte de confirmation affichée:", location.state);
      setShowConfirmationAlert(true);
      if (location.state?.email) {
        setEmail(location.state.email);
      }
    }
  }, [location.state]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      console.log("Tentative de connexion avec:", email);
      const { data, error } = await signIn(email, password);
      
      if (error) {
        console.error("Erreur de connexion détectée:", error.message);
        
        // Vérifier si l'erreur est liée à un email non confirmé
        if (
          error.message?.includes("Email not confirmed") || 
          error.message?.includes("Email non confirmé") ||
          error.message?.toLowerCase().includes("not confirmed") ||
          error.message?.includes("confirmer") ||
          error.message?.includes("vérifier")
        ) {
          setShowConfirmationAlert(true);
          toast({
            title: "Confirmation requise",
            description: "Veuillez confirmer votre adresse email avant de vous connecter. Vérifiez votre boîte de réception.",
            variant: "destructive",
            duration: 10000,
          });
          throw error;
        }
        
        throw error;
      }
      
      console.log("Connexion réussie:", data);
      
      toast({
        title: "Connexion réussie",
        description: "Redirection vers le tableau de bord...",
        variant: "default",
      });
      
      // Vérifier si l'utilisateur doit configurer une organisation
      const needsOrgSetup = localStorage.getItem("needsOrgSetup");
      if (needsOrgSetup === "true") {
        navigate("/organization-setup");
      } else {
        navigate("/dashboard");
      }
      
      onLoginSuccess();
    } catch (error: any) {
      console.error("Erreur de connexion:", error);
      toast({
        title: "Erreur de connexion",
        description: error.message || "Identifiants incorrects. Veuillez réessayer.",
        variant: "destructive",
        duration: 8000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form className="space-y-6" onSubmit={handleLogin}>
      {showConfirmationAlert && (
        <Alert variant="warning" className="mb-4 bg-amber-50 border-amber-200">
          <AlertCircle className="h-5 w-5 text-amber-600" />
          <AlertTitle className="text-amber-800 font-medium">Confirmation d'email requise</AlertTitle>
          <AlertDescription className="text-amber-700">
            Un email de confirmation a été envoyé à votre adresse. Veuillez vérifier votre boîte de réception et confirmer votre compte avant de vous connecter.
          </AlertDescription>
        </Alert>
      )}

      <div>
        <Label htmlFor="email" className="block text-sm font-medium text-gray-700">
          Adresse email
        </Label>
        <div className="mt-1 flex rounded-md shadow-sm">
          <span className="inline-flex items-center rounded-l-md border border-r-0 border-gray-300 bg-gray-50 px-3 text-gray-500">
            <Mail className="h-4 w-4" />
          </span>
          <Input
            id="email"
            name="email"
            type="email"
            required
            className="block w-full flex-1 rounded-none rounded-r-md border-gray-300 focus:border-socialsync-500 focus:ring-socialsync-500 sm:text-sm"
            placeholder="exemple@email.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>
      </div>

      <div>
        <Label htmlFor="password" className="block text-sm font-medium text-gray-700">
          Mot de passe
        </Label>
        <div className="mt-1 flex rounded-md shadow-sm">
          <span className="inline-flex items-center rounded-l-md border border-r-0 border-gray-300 bg-gray-50 px-3 text-gray-500">
            <Lock className="h-4 w-4" />
          </span>
          <Input
            id="password"
            name="password"
            type="password"
            required
            className="block w-full flex-1 rounded-none rounded-r-md border-gray-300 focus:border-socialsync-500 focus:ring-socialsync-500 sm:text-sm"
            placeholder="••••••••"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </div>
      </div>

      <div className="flex items-center justify-between">
        <div className="flex items-center">
          <input
            id="remember-me"
            name="remember-me"
            type="checkbox"
            className="h-4 w-4 rounded border-gray-300 text-socialsync-600 focus:ring-socialsync-500"
          />
          <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-900">
            Se souvenir de moi
          </label>
        </div>

        <div className="text-sm">
          <Link
            to="/forgot-password"
            className="font-medium text-socialsync-600 hover:text-socialsync-500"
          >
            Mot de passe oublié ?
          </Link>
        </div>
      </div>

      <div>
        <Button
          type="submit"
          className="group relative flex w-full justify-center rounded-md border border-transparent bg-socialsync-600 px-4 py-2 text-sm font-medium text-white hover:bg-socialsync-700 focus:outline-none focus:ring-2 focus:ring-socialsync-500 focus:ring-offset-2"
          disabled={isLoading}
        >
          <span className="absolute inset-y-0 left-0 flex items-center pl-3">
            <LogIn className="h-5 w-5 text-socialsync-500 group-hover:text-socialsync-400" />
          </span>
          {isLoading ? "Connexion en cours..." : "Se connecter"}
        </Button>
      </div>
    </form>
  );
};
